import React, { useState } from "react";
import {
  Container,
  Tabs,
  Tab,
  CircularProgress,
  TablePagination,
  Button,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import SearchAndFilters from "./SearchAndFilters";
import TransactionTable from "./TransactionTable";
import axios from "axios";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { DownloadIcon, PartnerArrowIcon } from "../../../res/NewWebIcons";
import { toast, ToastContainer } from "react-toastify";
import * as XLSX from 'xlsx';

const HierarchyReport = () => {
  const [tabValue, setTabValue] = useState("Pay-In");
  const [payInRecords, setPayInRecords] = useState([]);
  const [payOutRecords, setPayOutRecords] = useState([]);
  const [ftrRecords, setFtrRecords] = useState([]);
  const [beneficiaryRecords, setBeneficiaryRecords] = useState([]); // New state for beneficiary records
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    userType: "",
    selectedUserId: null,
    searchTerm: "",
    fromDate: "",
    toDate: "",
    searchBy: "", // Added for beneficiary search
  });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [activeTab, setActiveTab] = useState("CREDIT");
  
  // Separate states for FTR pagination
  const [ftrPage, setFtrPage] = useState(0);
  const [ftrSize, setFtrSize] = useState(10);
  const [totalFtrRecords, setTotalFtrRecords] = useState(0);
  
  // Separate states for Beneficiary pagination
  const [beneficiaryPage, setBeneficiaryPage] = useState(0);
  const [beneficiarySize, setBeneficiarySize] = useState(10);
  const [totalBeneficiaryRecords, setTotalBeneficiaryRecords] = useState(0);

  const handleTabClick = (transactionType) => {
    setActiveTab(transactionType);
    handleSearch(searchParams, page, size, transactionType);
  };

const handleSearch = async (
  params,
  page = 0,
  size = 10,
  transactionType = activeTab
) => {
  setLoading(true);
  const { userType, selectedUserId, searchTerm, fromDate, toDate, searchBy } = params;
  const jwtToken = sessionStorage.getItem("jwtToken");
  let apiEndpoint = "";

  const queryParams = {
    searchTerm,
    fromDate,
    toDate,
    userId: selectedUserId,
    page,
    size,
  };

  if (tabValue === "Pay-In") {
    apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllPyInTxnsByUserId";
  } else if (tabValue === "Pay-Out") {
    apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllPayoutTxnsByUserId";
  } else if (tabValue === "FTR") {
    apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/hierarchyFtrReport";
    queryParams.transactionType = transactionType;
  } else if (tabValue === "Beneficiary") {
    apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllHierarchyBeneficiaryTxn";
    queryParams.searchBy = searchBy;
  }

  try {
    const response = await axios.get(apiEndpoint, {
      headers: { Authorization: `Bearer ${jwtToken}` },
      params: queryParams,
    });

    // Reset all data
    setPayInRecords([]);
    setPayOutRecords([]);
    setFtrRecords([]);
    setBeneficiaryRecords([]);

    if (tabValue === "Pay-In") {
      const username = response.data.body?.sender_address?.username || '';
      const userAddress = response.data.body?.sender_address?.usersAddress || '';

      const records = response.data?.body?.data?.map((item) => ({
        id: item.id,
        txnid: item.txnid,
        easepayid: item.easepayid,
        cardType: item.cardType,
        upiVa: item.upiVa,
        cardnum: item.cardnum,
        phone: item.phone,
        addedon: item.addedon,
        netAmountDebit: item.netAmountDebit,
        amount: item.amount,
        status: item.status,
        userId: item.userId,
        mode: item.mode,
        settlement: item.settlement,
        name: username,
        userAddress: userAddress,
      })) || [];

      setPayInRecords(records);
      // Set total records for Pay-In
      setTotalRecords(response.data?.body?.totalRecords || records.length);
      setTotalPages(Math.ceil((response.data?.body?.totalRecords || records.length) / size));
    } 
    else if (tabValue === "Pay-Out") {
      const { transaction, sender_address } = response.data.body?.data || {};
      const records = transaction?.map((item) => ({
        ...item,
        sender_address: sender_address?.usersAddress || null,
        sender_name: sender_address?.username || null,
      })) || [];
      
      setPayOutRecords(records);
      // Set total records for Pay-Out
      setTotalRecords(response.data?.body?.totalRecords || records.length);
      setTotalPages(Math.ceil((response.data?.body?.totalRecords || records.length) / size));
    } 
    else if (tabValue === "FTR") {
      const records = response.data.body?.data?.map((item) => ({
        id: item.id,
        transactionId: item.transactionId,
        amount: item.amount,
        remark: item.remark,
        date: item.date,
        utype: item.utype,
        wOpeningBlc: item.wOpeningBlc,
        wClosingBlc: item.wClosingBlc,
        transactionType: item.transactionType,
        reciver: item.reciver,
        receiverUserName: item.receiverUserName,
        sender: item.sender,
        senderUserName: item.senderUserName,
      })) || [];
      
      setFtrRecords(records);
      setTotalFtrRecords(response.data.body?.totalRecords || 0);
    }
    else if (tabValue === "Beneficiary") {
      const records = response.data.body?.data?.map((item) => ({
        id: item.id,
        accountNo: item.accountNo,
        createdAt: item.createdAt,
        status: item.status,
        charges: item.charges,
        wOpeningBalance: item.wOpeningBalance,
        wClosingBalance: item.wClosingBalance,
        userid: item.userid,
      })) || [];
      
      setBeneficiaryRecords(records);
      setTotalBeneficiaryRecords(response.data.body?.totalElements || 0);
    }
  } catch (error) {
    console.error("Error fetching transaction data:", error);
    // Reset all data on error
    setPayInRecords([]);
    setPayOutRecords([]);
    setFtrRecords([]);
    setBeneficiaryRecords([]);
    setTotalRecords(0);
    setTotalPages(0);
    setCurrentPage(0);
    setTotalFtrRecords(0);
    setTotalBeneficiaryRecords(0);
  } finally {
    setLoading(false);
  }
}


  // Handlers for Beneficiary pagination
  const handleBeneficiaryPageChange = (event, newPage) => {
    setBeneficiaryPage(newPage);
    handleSearch(searchParams, newPage, beneficiarySize);
  };

  const handleBeneficiaryRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setBeneficiarySize(newSize);
    setBeneficiaryPage(0);
    handleSearch(searchParams, 0, newSize);
  };

  // Handlers for FTR pagination
  const handleFtrPageChange = (event, newPage) => {
    setFtrPage(newPage);
    handleSearch(searchParams, newPage, ftrSize); // Ensure you pass ftrSize for FTR specific pagination
  };

  const handleFtrRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setFtrSize(newSize);
    setFtrPage(1); // Reset to the first page
    handleSearch(searchParams, 0, newSize);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    handleSearch(searchParams, newPage, size);
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setSize(newSize);
    setPage(0);
    handleSearch(searchParams, 0, newSize);
  };

    const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPayInRecords([]);
    setPayOutRecords([]);
    setFtrRecords([]);
    setBeneficiaryRecords([]);
    setPage(0);
  };

  const downloadPDF = async () => {
    if (!searchParams.selectedUserId) {
      toast.error("Please select a user before downloading");
      return;
    }

    setLoading(true);
    try {
      const jwtToken = sessionStorage.getItem("jwtToken");
      let apiEndpoint = "";
      const params = {
        userId: searchParams.selectedUserId,
        fromDate: searchParams.fromDate,
        toDate: searchParams.toDate,
        searchBy: searchParams.searchBy,
      };

      if (tabValue === "Pay-In") {
        apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllPyInTxnsByUserIdtoexport";
      } else if (tabValue === "Pay-Out") {
        apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllPayoutTxnsByUserIdtoexport";
      } else if (tabValue === "FTR") {
        apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/hierarchyFtrReporttoexport";
        params.transactionType = activeTab;
      } else if (tabValue === "Beneficiary") {
        apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllHierarchyBeneficiaryReportToExport";
      }

      const response = await axios.get(apiEndpoint, {
        headers: { Authorization: `Bearer ${jwtToken}` },
        params,
      });

      if (response.data.statusCodeValue === 200) {
        const doc = new jsPDF('l', 'mm', 'a4');
        
        // Add header
        doc.setFontSize(20);
        doc.setTextColor(2, 123, 254); // Blue color
        doc.text(`Ipaisa ${tabValue} Transactions Report`, 14, 20);
        
        // Add date range if available
        if (searchParams.fromDate && searchParams.toDate) {
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.text(`Date Range: ${searchParams.fromDate} to ${searchParams.toDate}`, 14, 30);
        }
        
        // Add generation timestamp
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 40);
        
        // Add user information
        doc.setFontSize(12);
        doc.text(`User ID: ${searchParams.selectedUserId}`, 14, 50);

        let tableColumn = [];
        const tableRows = [];

        if (tabValue === "Pay-In") {
          tableColumn = [
            "Transaction ID", "User ID", "Date", 
            "PayIn Amount", "Amount", "Status", "Settlement"
          ];
          
          response.data.body.data.forEach((record) => {
            tableRows.push([
              record.txnid,
              record.userId,
              new Date(record.addedon).toLocaleString(),
              `₹${Number(record.netAmountDebit).toFixed(2)}`,
              `₹${Number(record.amount).toFixed(2)}`,
              record.status,
              record.settlement
            ]);
          });
        } 
        else if (tabValue === "Pay-Out") {
          tableColumn = [
            "External Ref", "Payee Account", "Payee Name", "Date",
            "Txn Charges", "Transfer Mode", "Wallet Opening Balance", "Txn.Value", 
            "Wallet Closing Balance","Refund ID", "Status"
          ];
          
          const transactions = response.data.body.data || [];
          transactions.forEach((record) => {
            tableRows.push([
              record.externalRef,
              record.payeeAccount,
              record.payeeName,
              new Date(record.timestamp).toLocaleString(),
              `₹${Number(record.txnCharges).toFixed(2)}`,
              record.transfer_mode,
              `₹${Number(record.walletOpeningBalance).toFixed(2)}`,
              `₹${Number(record.txnValue).toFixed(2)}`,
              `₹${Number(record.walletClosingBalance).toFixed(2)}`,
              record.refundRefId || 'N/A',
              record.status
            ]);
          });
        } 
        else if (tabValue === "FTR") {
          tableColumn = [
            "Transaction ID", "Sender", "Receiver", "Transaction Type",
            "Amount", "Date", "Opening Balance", 
            "Closing Balance", "Remark"
          ];
          
          response.data.body.data.forEach((record) => {
            tableRows.push([
              record.transactionId,
              record.sender,
              record.reciver,
              record.transactionType,
              `₹${Number(record.amount).toFixed(2)}`,
              new Date(record.date).toLocaleString(),
              `₹${Number(record.wOpeningBlc).toFixed(2)}`,
              `₹${Number(record.wClosingBlc).toFixed(2)}`,
              record.remark
            ]);
          });
        }
        else if (tabValue === "Beneficiary") {
          tableColumn = [
            "Account No", "Created At", "Opening Balance", "Charges", 
            "Closing Balance", "User ID", "Status"
          ];
          
          response.data.body.data.forEach((record) => {
            tableRows.push([
              record.accountNo,
              new Date(record.createdAt).toLocaleString(),
              `₹${Number(record.wOpeningBalance).toFixed(2)}`,
              `₹${Number(record.charges).toFixed(2)}`,
              `₹${Number(record.wClosingBalance).toFixed(2)}`,
              record.userid,
              record.status
            ]);
          });
        }

        doc.autoTable({
          head: [tableColumn],
          body: tableRows,
          startY: 60,
          theme: 'grid',
          styles: { 
            fontSize: 8, 
            cellPadding: 2,
            overflow: 'linebreak'
          },
          headerStyles: { 
            fillColor: [2, 123, 254],
            textColor: 255,
            fontStyle: 'bold'
          },
          alternateRowStyles: { fillColor: [240, 240, 240] },
          columnStyles: {
            0: { cellWidth: 25 },  // Transaction ID/External Ref/Account No
            1: { cellWidth: 25 },  // User ID/Payee Account/Created At
            2: { cellWidth: 30 },  // Date/Payee Name/Opening Balance
            3: { cellWidth: 20 },  // PayIn Amount/Txn Charges/Charges
            4: { cellWidth: 20 },  // Amount/Transfer Mode/Closing Balance
            5: { cellWidth: 20 },  // Status/Wallet Opening Balance/User ID
            6: { cellWidth: 20 },  // Settlement/Txn.Value/Status
            7: { cellWidth: 20 },  // Wallet Closing Balance
            8: { cellWidth: 25 }   // Status/Remark
          },
          margin: { top: 60 }
        });
        
        doc.save(`${tabValue}_Transactions_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      else if(response.data.statusCodeValue === 204) {
        setLoading(false);
        toast.error("No Data To Download");
      } else {
        setLoading(false);
        toast.error("Something Went Wrong In Backend");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    if (!searchParams.selectedUserId) {
      toast.error("Please select a user before downloading");
      return;
    }

    setLoading(true);
    try {
      const jwtToken = sessionStorage.getItem("jwtToken");
      let apiEndpoint = "";
      const params = {
        userId: searchParams.selectedUserId,
        fromDate: searchParams.fromDate,
        toDate: searchParams.toDate,
        searchBy: searchParams.searchBy,
      };

      if (tabValue === "Pay-In") {
        apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllPyInTxnsByUserIdtoexport";
      } else if (tabValue === "Pay-Out") {
        apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllPayoutTxnsByUserIdtoexport";
      } else if (tabValue === "FTR") {
        apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/hierarchyFtrReporttoexport";
        params.transactionType = activeTab;
      } else if (tabValue === "Beneficiary") {
        apiEndpoint = "https://api.ipaisa.co.in/myapp/admin/getAllHierarchyBeneficiaryReportToExport";
      }

      const response = await axios.get(apiEndpoint, {
        headers: { Authorization: `Bearer ${jwtToken}` },
        params,
      });

      if (response.data.statusCodeValue === 200) {
        let excelData = [];
        
        if (tabValue === "Pay-In") {
          excelData = response.data.body.data.map((record) => ({
            'Transaction ID': record.txnid,
            'User ID': record.userId,
            'Phone': record.phone,
            'Mode': record.mode,
            'Date': new Date(record.addedon).toLocaleString(),
            'PayIn Amount': Number(record.netAmountDebit),
            'Amount': Number(record.amount),
            'Status': record.status,
            'Settlement': record.settlement
          }));
        } 
        else if (tabValue === "Pay-Out") {
          const transactions = response.data.body.data || [];
          excelData = transactions.map((record) => ({
            'External Ref': record.externalRef,
            'Payer UID': record.userdetails?.userid || '',
            'Payer Name': record.userdetails?.name || '',
            'Payee Account': record.payeeAccount,
            'Payee Name': record.payeeName,
            'Date': new Date(record.timestamp).toLocaleString(),
            'Order ID': record.orderid,
            'Txn Charges': Number(record.txnCharges),
            'Transfer Mode': record.transfer_mode,
            'Wallet Opening Balance': `₹${Number(record.walletOpeningBalance).toFixed(2)}`,
            'Txn.Value': Number(record.txnValue),
            'Wallet Closing Balance': `₹${Number(record.walletClosingBalance).toFixed(2)}`,
            'Provider Name': record.providerName,
            'Refund ID': record.refundRefId || 'N/A',
            'Status': record.status
          }));
        } 
        else if (tabValue === "FTR") {
          excelData = response.data.body.data.map((record) => ({
            'Transaction ID': record.transactionId,
            'Sender': record.sender,
            'Sender Name': record.senderUserName,
            'Receiver': record.reciver,
            'Receiver Name': record.receiverUserName,
            'Date': new Date(record.date).toLocaleString(),
            'Transaction Type': record.transactionType,
            'Amount': Number(record.amount),
            'Opening Balance': `₹${Number(record.wOpeningBlc).toFixed(2)}`,
            'Closing Balance': `₹${Number(record.wClosingBlc).toFixed(2)}`,
            'Remark': record.remark
          }));
        }
        else if (tabValue === "Beneficiary") {
          excelData = response.data.body.data.map((record) => ({
            'Account No': record.accountNo,
            'Date': new Date(record.createdAt).toLocaleString(),
            'Opening Balance': `₹${Number(record.wOpeningBalance).toFixed(2)}`,
            'Charges': Number(record.charges),
            'Closing Balance': `₹${Number(record.wClosingBalance).toFixed(2)}`,
            'User ID': record.userid,
            'Status': record.status
          }));
        }

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        
        // Set column widths
        const wscols = Object.keys(excelData[0]).map(key => ({ wch: 20 }));
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `${tabValue} Transactions`);

        // Generate Excel file
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(data, `${tabValue}_Transactions_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if(response.data.statusCodeValue === 204) {
        toast.error("No Data To Download");
        setLoading(false);
      } else {
        setLoading(false);
        toast.error("Something Went Wrong In Backend");
      }
    } catch (error) {
      console.error("Error generating Excel:", error);
      toast.error("Failed to generate Excel file");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box style={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
      <Grid
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1%",
          justifyContent: "start",
        }}
      >
        <Typography
          color="textSecondary"
          style={{ fontFamily: "sans-serif", fontSize: "12px" }}
        >
          Reports
        </Typography>
        <PartnerArrowIcon />
        <Typography
          color="textSecondary"
          style={{ fontFamily: "sans-serif", fontSize: "12px" }}
        >
          Hierarchywise Report
        </Typography>
      </Grid>
      <Grid container spacing={2} sx={{ marginTop: "1%" }}>
        <Grid item xs={12} md={6}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontFamily: "sans-serif",
              fontSize: { xs: "16px", md: "19px" },
              color: "#343C6A",
              textAlign: { xs: "center", md: "left" },
            }}
          >
            Hierarchywise Report
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: { xs: "center", md: "flex-end" },
          }}
        >
          <Button
            onClick={downloadCSV}
            sx={{
              textTransform: "none",
              backgroundColor: "#027BFE",
              color: "#FFFFFF",
              borderRadius: "10px",
              padding: "8px 16px",
              fontFamily: "sans-serif",
              fontSize: { xs: "12px", md: "14px" },
              height: "40px",
              gap: 1,
            }}
          >
            <DownloadIcon />
            Download Excel
          </Button>
          <Button
            sx={{
              textTransform: "none",
              backgroundColor: "#027BFE",
              color: "#FFFFFF",
              borderRadius: "10px",
              padding: "8px 16px",
              fontFamily: "sans-serif",
              fontSize: { xs: "12px", md: "14px" },
              height: "40px",
              minWidth: { xs: "120px", sm: "140px" },
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
            onClick={downloadPDF}
          >
            <DownloadIcon />
            Download PDF
          </Button>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="start" alignItems="center" mb={2}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Pay-In" value="Pay-In" />
          <Tab label="Pay-Out" value="Pay-Out" />
          <Tab label="FTR" value="FTR" />
          <Tab label="Beneficiary" value="Beneficiary" /> {/* New tab */}
        </Tabs>
      </Box>
      {tabValue === "FTR" && (
        <Grid
          item
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "1rem",
          }}
        >
          <Button
            onClick={() => handleTabClick("DEBIT")}
            sx={{
              textTransform: "none",
              backgroundColor: activeTab === "DEBIT" ? "red" : "gray",
              color: "white",
              borderRadius: "10px",
              padding: "8px 16px",
              fontFamily: "sans-serif",
              fontSize: { xs: "12px", md: "14px" },
            }}
          >
            Debit
          </Button>
          <Button
            onClick={() => handleTabClick("CREDIT")}
            sx={{
              textTransform: "none",
              backgroundColor: activeTab === "CREDIT" ? "green" : "gray",
              color: "white",
              borderRadius: "10px",
              padding: "8px 16px",
              fontFamily: "sans-serif",
              fontSize: { xs: "12px", md: "14px" },
            }}
          >
            Credit
          </Button>
        </Grid>
      )}

      <SearchAndFilters
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        handleSearch={(params) => handleSearch(params, page, size)}
        tabValue={tabValue}
      />

      {loading ? (
        <CircularProgress sx={{ marginTop: "2%" }} />
      ) : (
        <>
          {tabValue === "Pay-In" ? (
            <TransactionTable
              userRecords={payInRecords}
              page={page}
              rowsPerPage={size}
              transactionType="Pay-In"
              selectedUserId={searchParams.selectedUserId}
              searchParams={searchParams}
            />
          ) : tabValue === "Pay-Out" ? (
            <TransactionTable
              userRecords={payOutRecords}
              page={page}
              rowsPerPage={size}
              transactionType="Pay-Out"
              selectedUserId={searchParams.selectedUserId}
              searchParams={searchParams}
            />
          ) : tabValue === "FTR" ? (
            <TransactionTable
              userRecords={ftrRecords}
              page={ftrPage}
              rowsPerPage={ftrSize}
              transactionType="FTR"
              selectedUserId={searchParams.selectedUserId}
              transactionTypee={activeTab}
              searchParams={searchParams}
            />
          ) : tabValue === "Beneficiary" ? (
            <TransactionTable
              userRecords={beneficiaryRecords}
              page={beneficiaryPage}
              rowsPerPage={beneficiarySize}
              transactionType="Beneficiary"
              selectedUserId={searchParams.selectedUserId}
              searchParams={searchParams}
            />
          ) : (
            <div>No data available</div>
          )}

          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ marginTop: "1%" }}
          >
<TablePagination
  component="div"
  count={tabValue === "FTR" ? totalFtrRecords : 
         tabValue === "Beneficiary" ? totalBeneficiaryRecords : 
         totalRecords}
  page={tabValue === "FTR" ? ftrPage : 
        tabValue === "Beneficiary" ? beneficiaryPage : 
        page}
  onPageChange={tabValue === "FTR" ? handleFtrPageChange : 
               tabValue === "Beneficiary" ? handleBeneficiaryPageChange : 
               handlePageChange}
  rowsPerPage={tabValue === "FTR" ? ftrSize : 
              tabValue === "Beneficiary" ? beneficiarySize : 
              size}
  onRowsPerPageChange={tabValue === "FTR" ? handleFtrRowsPerPageChange : 
                     tabValue === "Beneficiary" ? handleBeneficiaryRowsPerPageChange : 
                     handleRowsPerPageChange}
  rowsPerPageOptions={[10, 25, 50]}
  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
  labelRowsPerPage="Rows per page"
  SelectProps={{
    inputProps: { "aria-label": "rows per page" },
    native: true,
  }}
  nextIconButtonProps={{
    disabled: tabValue === "FTR" ? ftrPage >= Math.ceil(totalFtrRecords / ftrSize) - 1 :
            tabValue === "Beneficiary" ? beneficiaryPage >= Math.ceil(totalBeneficiaryRecords / beneficiarySize) - 1 :
            page >= Math.ceil(totalRecords / size) - 1
  }}
  backIconButtonProps={{
    disabled: tabValue === "FTR" ? ftrPage === 0 :
            tabValue === "Beneficiary" ? beneficiaryPage === 0 :
            page === 0
  }}
/>

          </Box>
        </>
      )}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="colored"
      />
    </Box>
  );
};

export default HierarchyReport;